import React, { useEffect, useState } from 'react'
import globalNotification from 'live/services/global-notification'
import { HotTable } from '@handsontable/react'
import { getDataset, uploadDataset, calculateActualForActivity } from '../api'
import { HotContainer } from '../commom'
import i18n from 'live/services/i18n'
import { StyledCardContainer, StyledButton, Actions, StyledOverlay } from '../styles/styles'
import LiveTooltip from 'live/components/ui/tooltip'
import { canEditWellPlanData } from '../permission/Permissions'
import { getVPHeight, classNames } from '../functions/functions'
import { LoadingSpinner } from 'plugin-wells/shared/components/LoadingSpinner'
import { DrillDownTable } from '../components/DrillDownTable'

interface Row {
    section: string
    plan_hours: number
    plan_depth_ft: number
    plan_days: number
    plan_days_cum: number
}

const emptyPlanDataset = Array.apply(null, Array(25)).map(() => ({
    section: null,
    plan_hours: null,
    plan_depth_ft: null,
    plan_days: null,
    plan_days_cum: null
}))

const datasetType = 'ds_day_depth_plan'

export const PlanDataset = ({ selected }) => {
    const [loading, setLoading] = useState<boolean>(false)
    const [data, setData] = useState<Row[]>(addCalculatedColumns(emptyPlanDataset))
    const ref = React.useRef<HotTable>(null)
    const { id, name } = selected?.asset
    const [collapsed, setCollapsed] = useState(false)
    const [dataCount, setDataRowCount] = useState<number>(0)
    const heightOfCentralComponents = getVPHeight() * 0.7
    const [containsUnsavedData, setContainsUnsavedData] = useState<boolean>(false)
    const [triggeredCalculation, setTriggeredCalculation] = useState<boolean>(false)
    const [showDrillDownTable, setShowDrillDownTable] = useState<boolean>(false)
    const [drillDownData, setDrillDownData] = useState<Record<string, any>>(undefined)
    const [drillDownDataTitle, setDrillDownDataTitle] = useState<string>('Drill Down Activity')

    function getData() {
        return ref.current.hotInstance.getSourceData()
    }

    function fetchData() {
        const controller = new AbortController()
        const signal = controller.signal
        setLoading(true)
        getDataset(signal, id, datasetType)
            .then(result => result.json())
            .then(data => {
                if (data.responseCode === 'OK') {
                    if (!data || !data.result || !data.result.data || !data.result.data.length) return
                    setDataRowCount(data.result.data.length)
                    setData(addCalculatedColumns(data.result.data))
                } else {
                    globalNotification.error(data.message ?? "We're having trouble retrieving the dataset")
                }
                setContainsUnsavedData(false)
                setLoading(false)
            })
            .catch(e => globalNotification.error(e.message ?? 'An error occurred on fetching dataset data'))
            .finally(() => {
                setLoading(false)
            })
    }

    useEffect(() => {
        fetchData()
    }, [])

    function save() {
        setLoading(true)
        uploadDataset(id, datasetType, data)
            .then(result => result.json())
            .then(response => {
                if (response.responseCode === 'OK') {
                    globalNotification.success('Dataset saved successfully')
                    setContainsUnsavedData(false)
                    //triggerActualCalculation()
                    setLoading(false)
                } else {
                    globalNotification.error(response.message ?? 'An error prevented the data from being saved')
                }
            })
            .catch(e => globalNotification.error(e.message ?? 'An error prevented the data from being saved'))
            .finally(() => setLoading(false))
    }

    function triggerActualCalculation() {
        setLoading(true)
        setTriggeredCalculation(true)
        calculateActualForActivity(id)
            .then(result => result.json())
            .then(response => {
                if (response.responseCode === 'OK') {
                    globalNotification.success('Actual values have been calculated successfully')
                    fetchData()
                } else {
                    globalNotification.error(response.message ?? 'An error prevented the data from being saved')
                }
            })
            .catch(e => globalNotification.error(e.message ?? 'An error prevented the data from being saved'))
            .finally(() => {
                setLoading(false)
                setTriggeredCalculation(false)
            })
    }

    function getRowCount() {
        const nonEmptyRowCount = ref.current?.hotInstance.countRows() ? ref.current?.hotInstance.countRows() : 0
        const emptyRowCount = ref.current?.hotInstance.countEmptyRows() ? ref.current?.hotInstance.countEmptyRows() : 0

        return nonEmptyRowCount - emptyRowCount
    }

    function addCalculatedColumns(data): Row[] {
        const convertedData = _.cloneDeep(data)
        let sum_plan_hours = 0
        let sum_actual_hours = 0
        let sum_ahead_and_behind_hours = 0

        for (let i = 0; i < _.size(data); i++) {
            const entry = convertedData[i]
            const plan_hours = parseFloat(entry?.plan_hours)
            const actual_hours = entry?.actual_hours ? parseFloat(entry?.actual_hours) : 0

            try {
                _.set(entry, 'actual_hours', _.round(actual_hours, 2))
                if (plan_hours != undefined && plan_hours != null && _.isFinite(plan_hours)) {
                    sum_plan_hours = sum_plan_hours + plan_hours
                    _.set(entry, 'plan_days', _.round(plan_hours / 24, 2))
                    _.set(entry, 'plan_days_cum', _.round(sum_plan_hours / 24, 2))
                } else {
                    _.set(entry, 'plan_days', null)
                    _.set(entry, 'plan_days_cum', null)
                }

                if (actual_hours != undefined && actual_hours != null && _.isFinite(actual_hours)) {
                    sum_actual_hours = sum_actual_hours + actual_hours
                    _.set(entry, 'actual_days', _.round(actual_hours / 24, 2))
                    _.set(entry, 'actual_days_cum', _.round(sum_actual_hours / 24, 2))
                } else {
                    _.set(entry, 'actual_days', null)
                    _.set(entry, 'actual_days_cum', null)
                }

                if (
                    actual_hours != undefined &&
                    actual_hours != null &&
                    _.isFinite(actual_hours) &&
                    plan_hours != undefined &&
                    plan_hours != null &&
                    _.isFinite(plan_hours)
                ) {
                    sum_ahead_and_behind_hours = sum_ahead_and_behind_hours + (plan_hours - actual_hours)
                    _.set(entry, 'ahead_and_behind_days_cum', _.round(sum_ahead_and_behind_hours / 24, 2))
                } else {
                    _.set(entry, 'ahead_and_behind_days_cum', null)
                }
            } catch (error) {
                console.error(error)
            }
        }

        return convertedData
    }

    return (
        <div>
            {showDrillDownTable && (
                <DrillDownTable
                    tableData={drillDownData}
                    modalTitle={drillDownDataTitle}
                    showModal={showDrillDownTable}
                    setShowModal={setShowDrillDownTable}
                ></DrillDownTable>
            )}
            <StyledCardContainer
                title={i18n('Well Plan Data')}
                collapsed={collapsed}
                toolTip={() => {
                    return (
                        <>
                            <StyledButton className="trans">
                                <LiveTooltip
                                    position={'left'}
                                    content={i18n('This is where you can view and edit well plan data.')}
                                >
                                    <i className="fa fa-question-circle-o"></i>
                                </LiveTooltip>
                            </StyledButton>
                        </>
                    )
                }}
                renderControls={() => {
                    return (
                        <>
                            <StyledButton disabled={true} className="trans">
                                <i class="fa fa-table"></i> Available Row(s) : {dataCount}
                            </StyledButton>
                            {canEditWellPlanData() && (
                                <StyledButton
                                    onClick={() => triggerActualCalculation()}
                                    className={classNames({
                                        solid: true
                                    })}
                                    disabled={triggeredCalculation}
                                >
                                    <i
                                        className={classNames({
                                            'fa fa-gear': true,
                                            'need-to-rotate': triggeredCalculation
                                        })}
                                    />
                                    Calculate Actual Data
                                </StyledButton>
                            )}
                        </>
                    )
                }}
            >
                <StyledOverlay>
                    {loading && (
                        <StyledOverlay className="inner" style={{ height: heightOfCentralComponents }}>
                            <LoadingSpinner message="Loading..." />
                        </StyledOverlay>
                    )}
                    <HotContainer basic={true}>
                        <HotTable
                            ref={ref}
                            data={data}
                            settings={{
                                colHeaders: true,
                                rowHeaders: true,
                                readOnlyCellClassName: 'is-readOnly',
                                columns: [
                                    { data: 'section', readOnly: !canEditWellPlanData() },
                                    { data: 'subactivity', wordWrap: true, readOnly: !canEditWellPlanData() },
                                    {
                                        data: 'plan_depth_ft',
                                        validator: 'numeric',
                                        allowInvalid: false,
                                        readOnly: !canEditWellPlanData()
                                    },
                                    {
                                        data: 'plan_hours',
                                        validator: 'numeric',
                                        allowInvalid: false,
                                        readOnly: !canEditWellPlanData()
                                    },
                                    {
                                        data: 'actual_hours',
                                        validator: 'numeric',
                                        allowInvalid: false,
                                        readOnly: false
                                    },
                                    { data: 'plan_days', readOnly: true },
                                    { data: 'actual_days', readOnly: true },
                                    { data: 'plan_days_cum', readOnly: true },
                                    { data: 'actual_days_cum', readOnly: true },
                                    { data: 'ahead_and_behind_days_cum', readOnly: true }
                                ],
                                colHeaders: [
                                    'Phase',
                                    'Sub Activity',
                                    'Plan Depth (ft)',
                                    'Plan Hours',
                                    'Actual Hours',
                                    'Plan Days',
                                    'Actual Days',
                                    'Plan Cum. Days',
                                    'Actual Cum. Days',
                                    'Ahead & Behind Cum. Days'
                                ],
                                allowEmpty: true,
                                height: heightOfCentralComponents,
                                allowInsertColumn: false,
                                autoColumnSize: true,
                                columnSorting: true,
                                stretchH: 'all',
                                autoRowSize: true,
                                beforePaste: (data, coords) => {
                                    // data = data.map(innerArr => innerArr.map(str => str.replace(/\s+|\t|\n/g, ' ')))
                                    // return data
                                    setLoading(true)
                                    let i = data.length
                                    while (i--) {
                                        let allFieldsEmpty = true
                                        for (var j = 0; j < data[i].length; j++) {
                                            data[i][j] = data[i][j].trim()
                                            data[i][j] = data[i][j].replace(/\s+|\t|\n/g, ' ')
                                            if (allFieldsEmpty && data[i][j] && data[i][j].length > 0) {
                                                allFieldsEmpty = false
                                            }
                                        }
                                        //Removes empty lines
                                        if (allFieldsEmpty) {
                                            data.splice(i, 1)
                                        }
                                    }

                                    setLoading(false)
                                },
                                afterChange: (changes, source) => {
                                    if (
                                        source === 'edit' ||
                                        source === 'CopyPaste.paste' ||
                                        source === 'Autofill.fill'
                                    ) {
                                        const newLocalData = addCalculatedColumns(getData())
                                        if (!_.isEqual(data, newLocalData)) {
                                            setData(newLocalData)
                                        }

                                        if (changes) {
                                            changes.forEach(([row, prop, oldValue, newValue]) => {
                                                if (String(oldValue) !== String(newValue)) {
                                                    setContainsUnsavedData(true)
                                                }
                                            })
                                        }
                                    }

                                    setDataRowCount(getRowCount())
                                },
                                afterRemoveRow: (index, amount) => {
                                    setContainsUnsavedData(true)
                                    setDataRowCount(dataCount - amount)
                                    try {
                                        setData(addCalculatedColumns(getData()))
                                    } catch {}
                                },
                                contextMenu: {
                                    callback(key, selection, clickEvent) {},
                                    items: {
                                        drill_down_actual: {
                                            name() {
                                                return '<b>Drill-down actual hours</b>'
                                            },
                                            hidden() {
                                                return this.getSelectedLast()[1] == 0
                                            },
                                            callback(key, selection, clickEvent) {
                                                const rowData = ref.current?.hotInstance.getSourceDataAtRow(
                                                    selection[0].start.row
                                                )
                                                setDrillDownData(rowData)
                                                setShowDrillDownTable(true)
                                            },
                                            disabled: function () {
                                                const selectionRows = this.getSelectedRangeLast()
                                                let disable = true
                                                const t = ref.current?.hotInstance.getDataAtCell(
                                                    selectionRows.from.row,
                                                    4
                                                )

                                                if (t && t > 0) {
                                                    disable = false
                                                }
                                                return selectionRows.from.row !== selectionRows.to.row || disable
                                            }
                                        },
                                        sepratorOne: '---------',
                                        row_above: {
                                            name() {
                                                return 'Insert row above'
                                            }
                                        },
                                        row_below: {
                                            name() {
                                                return 'Insert row below'
                                            }
                                        },
                                        remove_row: {
                                            name() {
                                                return 'Remove row(s)'
                                            }
                                        }
                                    }
                                }
                            }}
                        ></HotTable>
                    </HotContainer>

                    <Actions>
                        {canEditWellPlanData() && (
                            <button
                                className="btn btn-primary"
                                onClick={save}
                                disabled={loading || !containsUnsavedData}
                            >
                                Save
                            </button>
                        )}
                    </Actions>
                </StyledOverlay>
            </StyledCardContainer>
        </div>
    )
}
